import { effectByKey } from "./effect-registry.js";
import { compileMultiActionRoute } from "./compile/route-multi.js";
export function newAutomationGroupId() {
    return `ag_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}
/** One authored Automation → N CreateRuleAttachmentInputs (one per branch).
 *  Shared sections (name, active, stages, override) stamp onto every branch;
 *  automationGroupId is added only when there is more than one branch (or a
 *  groupId already exists) — single-branch output is byte-compatible with
 *  today's shape. */
export function compileAutomation(a) {
    // A zero-branch automation would compile to `[]`, and passing `[]` to
    // `replaceAutomationRules` silently wipes the group's rules — deleting the
    // automation with no warning. An automation must always keep ≥1 if/then.
    if (a.branches.length === 0) {
        throw new Error("An automation must have at least one branch (if/then).");
    }
    const groupId = a.branches.length > 1 ? (a.groupId ?? newAutomationGroupId()) : a.groupId;
    return a.branches.map((branch, index) => {
        const base = compileBranch(a, branch);
        const config = {
            ...base.config,
            ...(groupId ? { automationGroupId: groupId } : {}),
            ...(a.description ? { automationDescription: a.description } : {}),
        };
        // stopAfterFirstMatch is automation-level: the flag on `a` is the single
        // source of truth. Delete first — a config-check branch's parse round-trips
        // rule.config wholesale into branch config (`rest`), so without the delete
        // a CLEARED flag would silently resurrect on recompile.
        delete config.stopAfterFirstMatch;
        if (a.stopAfterFirstMatch)
            config.stopAfterFirstMatch = true;
        return {
            ...base,
            name: a.name,
            isActive: a.isActive,
            displayOrder: index,
            // stages[] on CHECK branches is the effectiveValidateStages machinery;
            // `stage` mirrors stages[0] to keep the legacy single-stage field's
            // "first selected" contract (runtime prefers stages[], so no behavior
            // change — but stored rows and any stage-reader stay consistent).
            ...(branch.family === "check" && a.stages?.length
                ? { stages: a.stages, stage: a.stages[0] }
                : {}),
            // R1/R2 (E-062) — stages[] on ROUTE branches carries the step scoping
            // for the two step-identity moments: step_enter ("enters step X",
            // filtered by the entered step) and on_validate ("accepted at step X",
            // filtered by the accepted step). The legacy `stage` field stays
            // MOMENT-derived (route firing is trigger-driven). A route automation
            // without stages compiles byte-identical to pre-R1 (deploy no-flip).
            ...(branch.family === "route" &&
                (a.moment === "step_enter" || a.moment === "on_validate") &&
                a.stages?.length
                ? { stages: a.stages }
                : {}),
            overrideBehavior: a.overrideBehavior,
            overrideConfig: a.overrideConfig,
            // Applies to → Roles: automation-level, stamped top-level onto every
            // member (parse never round-trips top-level unknowns, so absent = absent).
            ...(a.appliesToRoles?.length ? { appliesToRoles: a.appliesToRoles } : {}),
            config,
        };
    });
}
function compileBranch(a, b) {
    if (b.family === "route") {
        return compileMultiActionRoute({
            name: a.name,
            severity: b.severity,
            overrideBehavior: a.overrideBehavior,
            overrideConfig: a.overrideConfig,
            moment: a.moment,
            condition: b.condition,
        }, b.actions);
    }
    const effect = effectByKey(b.effectKey);
    if (!effect?.compile)
        throw new Error(`Unknown check effect: ${b.effectKey}`);
    const input = effect.compile({
        effectKey: b.effectKey,
        moment: a.moment,
        scope: a.scope,
        fieldName: a.fieldName,
        config: b.config,
        message: b.message,
        condition: b.condition,
    });
    return { ...input, severity: b.severity };
}
//# sourceMappingURL=compile-automation.js.map